import {
  useSurfacePreferences,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { SettingsRow } from "./SettingsRow.tsx";

/**
 * The two switches governing email drafts the assistant writes for the user:
 * where they land and whether the mail carries the Okthx footer.
 *
 * "Save drafts to your mailbox" is named for the mailbox generically rather than
 * Gmail: the mirror dispatches on the sending connector's provider, so a
 * Microsoft account gets Outlook drafts.
 *
 * The footer switch reaches every email the assistant sends to another person,
 * which is why the copy names the audience rather than a kind of draft. It does
 * not reach notifications and routine reports sent to the user themselves —
 * those carry an attribution footer naming the routine, which is addressed to
 * the person reading it.
 *
 * Both are on by default.
 */
export const EmailDraftsPreferences = () => {
  const prefs = useSurfacePreferences("communications");
  const updatePreferences = useUpdateSurfacePreferences("communications");

  const save = async (delta: {
    saveDraftsToMailbox?: boolean;
    showPromoFooterOnReplies?: boolean;
  }) => {
    try {
      await updatePreferences(delta);
    } catch {
      errorToast("Failed to update draft settings. Please try again.");
    }
  };

  return (
    <>
      <SettingsRow
        title="Save drafts to your mailbox"
        description="Email drafts waiting on you are also saved to Gmail or Outlook, so you can review and send them from your email app."
        checked={prefs.saveDraftsToMailbox}
        onCheckedChange={(checked) => void save({ saveDraftsToMailbox: checked })}
      />
      <SettingsRow
        title="Add Okthx footer to emails you send"
        description="Emails the assistant sends to other people end with a small Okthx footer."
        checked={prefs.showPromoFooterOnReplies}
        onCheckedChange={(checked) => void save({ showPromoFooterOnReplies: checked })}
      />
    </>
  );
};
